import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";

const KEYS = ["Tab", "Enter", "Backspace", "Escape", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"];

// A keyed inner component drops screenshots, drafts and pending response state on
// an auth/tenant change. No effect or render starts or disconnects a browser.
export default function HostedGotSportConnectionCard({ token, orgId, onReviewSubmitted }) {
  return <HostedSession key={JSON.stringify([orgId, token])} token={token} onReviewSubmitted={onReviewSubmitted} />;
}

function HostedSession({ token, onReviewSubmitted }) {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("Not connected. Start a temporary browser when you are ready.");
  const [text, setText] = useState("");
  const [consent, setConsent] = useState(false);
  const [key, setKey] = useState("Enter");
  const [records, setRecords] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [reviewResult, setReviewResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const request = async (action) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    if (action?.action === "collect") {
      setRecords(null);
      setPreviewId(null);
      setReviewResult(null);
    }
    try {
      if (action?.action === "review") {
        setSubmitting(true);
        const response = await axios.post(`${API}/connect/hosted-gotsport/sessions/${encodeURIComponent(session.session_id)}/review`, { preview_id: previewId }, { headers: { Authorization: `Bearer ${token}` } });
        if (!mounted.current) return;
        if (response.data?.status !== "review_required") throw new Error("unexpected_review_status");
        setReviewResult(response.data);
        try {
          await onReviewSubmitted?.();
        } catch {
          setError("The review was submitted, but Connect could not refresh. Open Review queue or Sync history to reload the latest results.");
        }
        return;
      }
      const response = await axios.post(`${API}/connect/hosted-gotsport/sessions${session ? `/${encodeURIComponent(session.session_id)}/actions` : ""}`, action || {}, { headers: { Authorization: `Bearer ${token}` } });
      if (action?.action === "disconnect") {
        if (response.data.status !== "disconnected") throw new Error("cleanup_pending");
        setNotice("Disconnected. The hosted browser has closed.");
        setSession(null);
        setRecords(null);
        setPreviewId(null);
        setReviewResult(null);
        setText("");
        setConsent(false);
        return;
      }
      setSession((current) => ({ ...current, ...response.data }));
      if (action?.action === "collect" && response.data.status === "preview_ready") {
        setRecords(response.data.records || []);
        setPreviewId(typeof response.data.preview_id === "string" && /^[a-f0-9]{32}$/.test(response.data.preview_id) ? response.data.preview_id : null);
      }
    } catch (requestError) {
      if (requestError.response?.status === 410) {
        setSession(null);
        setRecords(null);
        setPreviewId(null);
        setReviewResult(null);
        setText("");
        setConsent(false);
        setError("The hosted session has ended. Start a new hosted browser to continue.");
        return;
      }
      setError(action?.action === "review" ? "Review submission was not confirmed. Retry this preview, or collect again if it is no longer available. Check your access if the service is unavailable." : action?.action === "disconnect" || (session && requestError.response?.status === 503) ? "Cleanup is not confirmed. Keep this session open and retry Disconnect hosted browser." : session ? "The browser action failed. Refresh the screen before retrying; your session is still available." : "Hosted GotSport could not be started. Check your access or try again when the hosted service is available.");
    } finally {
      setBusy(false);
      setSubmitting(false);
    }
  };
  const clickScreen = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const x = Math.max(0, Math.min(session.screen.width - 1, Math.floor((event.clientX - bounds.left) * session.screen.width / bounds.width)));
    const y = Math.max(0, Math.min(session.screen.height - 1, Math.floor((event.clientY - bounds.top) * session.screen.height / bounds.height)));
    request({ action: "click", x, y });
  };
  const sendText = () => {
    if (!consent || !text || busy) return;
    const value = text;
    setText("");
    request({ action: "type", text: value });
  };
  return <article className="min-w-0 rounded-[24px] border border-[#dfe6df] bg-white p-4 sm:p-6 lg:col-span-2" data-testid="hosted-gotsport-connection-card" aria-busy={busy}>
    <h2 className="text-xl font-semibold text-[#102a27]">GotSport</h2>
    <p className="mt-2 max-w-prose text-sm text-[#60706a]">Open a temporary hosted browser to sign in and preview your teams. No extension is required.</p>
    <p className="mt-2 max-w-prose text-sm text-[#60706a]">This is a remote browser, not your local GotSport tab. Text you choose to send, including login details, passes through the hosted service to GotSport. Only start if you are authorized to access this account.</p>
    {error && <p role="alert" className="mt-4 text-sm text-[#9b352a]">{error}</p>}
    {notice && <p aria-live="polite" className="mt-4 text-sm text-[#60706a]">{notice}</p>}
    {!session ? <Button className="mt-4" type="button" onClick={() => request()} disabled={busy}>{busy ? "Starting…" : "Start hosted GotSport"}</Button> : <>
      <p className="mt-4 max-w-prose text-sm text-[#60706a]">Complete sign-in and any MFA yourself in the hosted GotSport browser. Then collect a preview from the configured organization’s teams page. Check the team names before submitting for review; nothing is imported automatically.</p>
      <p className="mt-4 text-sm text-[#60706a]" id="hosted-screen-help">Tap a field in the screen, then send text below. Use Tab and Enter for keyboard navigation. Refresh to see changes; the image is not a live video.</p>
      <button type="button" aria-label="Interact with hosted GotSport" aria-describedby="hosted-screen-help" disabled={busy} onClick={clickScreen} className="mt-3 block w-full overflow-hidden rounded-xl border-0 p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0b654f] disabled:opacity-60">
        <img src={`data:image/png;base64,${session.screen.image}`} alt="Hosted GotSport screen" className="block h-auto w-full" draggable={false} />
      </button>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => request({ action: "disconnect" })} disabled={busy}>Disconnect hosted browser</Button>
        <Button type="button" variant="outline" onClick={() => request({ action: "screen" })} disabled={busy}>Refresh screen</Button>
        <Button type="button" variant="outline" onClick={() => request({ action: "scroll", deltaY: -500 })} disabled={busy}>Scroll up</Button>
        <Button type="button" variant="outline" onClick={() => request({ action: "scroll", deltaY: 500 })} disabled={busy}>Scroll down</Button>
      </div>
      <Button type="button" className="mt-4" onClick={() => request({ action: "collect" })} disabled={busy}>Collect teams preview</Button>
      <fieldset disabled={busy} className="mt-5 min-w-0 space-y-3 border-t border-[#dfe6df] pt-4">
        <legend className="font-semibold text-[#102a27]">Browser controls</legend>
        <label className="block text-sm text-[#102a27]">Text to send to GotSport<input type="password" autoComplete="off" maxLength={1024} value={text} onChange={(event) => setText(event.target.value)} className="mt-1 block min-h-11 w-full rounded-lg border border-[#cbd7cf] px-3 text-base" /></label>
        <label className="flex items-start gap-3 text-sm text-[#52615b]"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} className="mt-1 h-4 w-4 shrink-0" />I consent to sending the text I enter, including credentials, through the hosted service to GotSport.</label>
        <Button type="button" disabled={busy || !consent || !text} onClick={sendText}>Send text</Button>
        <div className="flex flex-wrap items-end gap-2"><label className="text-sm text-[#102a27]">Browser key<select value={key} onChange={(event) => setKey(event.target.value)} className="mt-1 block min-h-11 rounded-lg border border-[#cbd7cf] bg-white px-3">{KEYS.map((value) => <option key={value}>{value}</option>)}</select></label><Button type="button" variant="outline" onClick={() => request({ action: "key", key })}>Send {key}</Button></div>
      </fieldset>
      {records !== null && <section className="mt-6 border-t border-[#dfe6df] pt-4" aria-label="Teams preview">
        <h3 className="font-semibold text-[#102a27]">Teams preview</h3>
        {reviewResult ? <p role="status" className="mt-1 text-sm text-[#60706a]">Review required. Teams submitted for review, not imported. Canonical teams have not changed.</p> : <p className="mt-1 text-sm text-[#60706a]">Read-only preview. Not staged or imported. No Sporthouse records have changed.</p>}
        {previewId && records.length > 0 && <Button type="button" className="mt-4" onClick={() => request({ action: "review" })} disabled={busy || !!reviewResult}>{reviewResult ? "Submitted for review" : submitting ? "Submitting for review…" : "Submit teams for review"}</Button>}
        {records.length === 0 ? <p className="mt-3 text-sm">No teams were found on this page. Navigate to your teams in GotSport and collect again.</p> : <ul className="mt-3 divide-y divide-[#dfe6df]">{records.map((record, index) => {
          const team = record.payload || record;
          return <li key={index} className="py-3 text-sm break-words"><p className="font-semibold text-[#102a27]">{typeof team.name === "string" ? team.name : "Unnamed team"}</p><p className="text-[#60706a]">{[team.age_group, team.gender].filter((value) => typeof value === "string").join(" · ")}</p></li>;
        })}</ul>}
      </section>}
    </>}
  </article>;
}
